import { CalendarClock, CheckCircle2, FileText, Landmark, Mail, type LucideIcon } from 'lucide-react';
import type { UpsellPaymentBranding } from '../api/paymentApi';
import {
  DEFAULT_PAYMENT_THEME,
  formatEuro,
  type PaymentAmounts,
  type PaymentContact,
  type PaymentMethodKind,
  type PaymentTheme,
} from '../utils/paymentDisplay';
import { PaymentStatusScreen } from './PaymentStatusScreen';
import { ContactLine, IconBadge } from './PaymentUi';

interface PaymentSuccessProps {
  theme?: PaymentTheme;
  footerBranding?: UpsellPaymentBranding | null;
  methodKind?: PaymentMethodKind;
  amounts?: PaymentAmounts | null;
  warrantyName?: string | null;
  contact?: PaymentContact | null;
}

/**
 * Page de confirmation affichée après un paiement (ou un mandat SEPA) réussi,
 * aux couleurs du garant quand elles sont connues, avec les prochaines étapes.
 */
export function PaymentSuccess({
  theme = DEFAULT_PAYMENT_THEME,
  footerBranding = null,
  methodKind = 'card',
  amounts = null,
  warrantyName = null,
  contact = null,
}: PaymentSuccessProps) {
  const isMandate = methodKind === 'sepa_mandate';
  const forWarranty = warrantyName ? ` pour votre garantie ${warrantyName}` : '';

  const steps: Array<{ icon: LucideIcon; title: string; text: string }> = [
    {
      icon: Mail,
      title: 'E-mail de confirmation',
      text: 'Vous allez recevoir un e-mail confirmant votre souscription.',
    },
    {
      icon: FileText,
      title: 'Vos documents contractuels',
      text: 'Vos documents contractuels vous seront envoyés par e-mail. Conservez-les précieusement.',
    },
  ];
  if (isMandate) {
    steps.push({
      icon: CalendarClock,
      title: 'Prélèvements mensuels',
      text:
        amounts?.kind === 'monthly'
          ? `Vos mensualités de ${formatEuro(amounts.monthlyAmount)} seront prélevées automatiquement selon l’échéancier de votre contrat. Vous serez prévenu avant chaque prélèvement.`
          : 'Vos mensualités seront prélevées automatiquement selon l’échéancier de votre contrat. Vous serez prévenu avant chaque prélèvement.',
    });
  }
  if (methodKind === 'sepa_single') {
    steps.push({
      icon: Landmark,
      title: 'Prélèvement SEPA',
      text: 'Le prélèvement apparaîtra sur votre relevé bancaire sous quelques jours ouvrés.',
    });
  }

  return (
    <PaymentStatusScreen
      theme={theme}
      footerBranding={footerBranding}
      icon={CheckCircle2}
      tone="success"
      title={isMandate ? 'Mandat de prélèvement enregistré' : 'Paiement confirmé'}
      description={
        <p className="tw-mb-0">
          {isMandate
            ? `Votre mandat de prélèvement SEPA a bien été enregistré${forWarranty}.`
            : `Votre paiement a bien été enregistré${forWarranty}.`}{' '}
          Merci pour votre confiance.
        </p>
      }
    >
      <div className="tw-mt-8 tw-text-left">
        <h2 className="tw-mb-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-600">
          Et maintenant ?
        </h2>
        <ol className="tw-mb-0 tw-list-none tw-space-y-3 tw-pl-0">
          {steps.map((step) => (
            <li
              key={step.title}
              className="tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-slate-200 tw-px-4 tw-py-3"
            >
              <IconBadge icon={step.icon} size="sm" />
              <div className="tw-min-w-0">
                <p className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-gray-900">{step.title}</p>
                <p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-gray-600">{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <p className="tw-mb-0 tw-mt-6 tw-text-sm tw-text-gray-600">Vous pouvez fermer cette page en toute sécurité.</p>

      {contact && <ContactLine contact={contact} align="center" className="tw-mt-6 tw-border-t tw-border-slate-100 tw-pt-6" />}
    </PaymentStatusScreen>
  );
}
